import { Link, useParams } from '@tanstack/react-router';
import { DownloadIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError, api } from '@/lib/api';
import { type FindingOut, useAudit } from '@/lib/hooks/useAudits';
import { cn } from '@/lib/utils';

/**
 * PDF download path: the API endpoint is auth-gated, so an `<a href>` click
 * (which doesn't carry the Authorization header) would 401. We fetch a
 * pre-signed S3 URL via the auth'd API helper, then open that — the S3 URL
 * doesn't require any header, so window.open + S3 work cleanly.
 */
async function downloadPdf(auditId: string): Promise<void> {
  try {
    const res = await api<{ url: string }>(`/api/audits/${auditId}/pdf-url`);
    window.open(res.url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    const msg =
      err instanceof ApiError
        ? `${err.status}: ${JSON.stringify(err.detail)}`
        : (err as Error).message;
    alert(`Couldn't fetch the PDF — ${msg}`);
  }
}

const SEVERITY_RANK: Record<FindingOut['severity'], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

const SEVERITY_STYLES: Record<FindingOut['severity'], string> = {
  critical: 'bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200',
  high: 'bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-200',
  medium: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  info: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

export function AuditDetailPage() {
  const { auditId } = useParams({ strict: false }) as { auditId: string };
  const isTerminal = (s?: string) => s === 'complete' || s === 'failed';
  const { data, isLoading } = useAudit(auditId, {
    refetchInterval: (q) => (isTerminal(q.state.data?.status) ? false : 3000),
  });

  const [tab, setTab] = useState<'overview' | 'findings' | 'fix_plan' | 'raw'>('overview');

  if (isLoading || !data) {
    return <div className="mx-auto max-w-6xl px-8 py-10 text-sm text-slate-500">Loading audit…</div>;
  }

  const running = data.status === 'queued' || data.status === 'running';
  const scoreColor =
    data.overall_score == null ? 'text-slate-400'
    : data.overall_score <= 40 ? 'text-[var(--color-critical)]'
    : data.overall_score <= 70 ? 'text-[var(--color-warning)]'
    : 'text-[var(--color-success)]';

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <header className="mb-6 flex items-end justify-between">
        <div>
          <p className="text-sm text-slate-500">
            <Link to="/app" className="underline-offset-4 hover:underline">Dashboard</Link>
            {' · '}Audit
          </p>
          <h1 className="mt-1 font-mono text-xl tracking-tight">{data.id.slice(0, 8)}</h1>
        </div>
        {data.has_pdf && (
          <Button onClick={() => downloadPdf(data.id)}>
            <DownloadIcon className="h-4 w-4" /> Download PDF
          </Button>
        )}
      </header>

      {running && (
        <Card className="mb-4 border-[var(--color-accent)]">
          <p className="text-sm">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[var(--color-accent)]" />{' '}
            Audit is {data.status}. This page refreshes automatically.
          </p>
        </Card>
      )}
      {data.status === 'failed' && (
        <Card className="mb-4 border-[var(--color-critical)]">
          <CardHeader>
            <CardTitle>Audit failed</CardTitle>
            <CardDescription>{data.error_message ?? 'Unknown error'}</CardDescription>
          </CardHeader>
        </Card>
      )}

      <nav className="mb-6 flex gap-2 border-b border-slate-200 dark:border-slate-800">
        {(['overview', 'findings', 'fix_plan', 'raw'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'border-b-2 px-4 py-2 text-sm transition-colors',
              tab === t
                ? 'border-[var(--color-accent)] text-slate-900 dark:text-slate-100'
                : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-slate-100',
            )}
          >
            {t === 'overview' ? 'Overview'
              : t === 'findings' ? 'Findings'
              : t === 'fix_plan' ? 'Fix plan'
              : 'Raw JSON'}
          </button>
        ))}
      </nav>

      {tab === 'overview' && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle>Overall score</CardTitle>
              <CardDescription>Weighted across 10 dimensions.</CardDescription>
            </CardHeader>
            <div className={cn('text-6xl font-semibold', scoreColor)}>
              {data.overall_score ?? '—'}
              <span className="ml-1 text-2xl text-slate-400">/100</span>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              {data.score_category ?? 'Awaiting completion.'}
            </p>
          </Card>
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Dimensions</CardTitle>
              <CardDescription>Per-dimension scores (0–10).</CardDescription>
            </CardHeader>
            <DimensionsTable results={data.results_json} />
          </Card>
        </div>
      )}

      {tab === 'findings' && <FindingsList findings={data.findings} />}

      {tab === 'fix_plan' && <FixPlan results={data.results_json} />}

      {tab === 'raw' && (
        <Card>
          <pre className="overflow-x-auto whitespace-pre rounded-md bg-slate-900 p-4 text-xs text-slate-100">
{JSON.stringify(data.results_json, null, 2)}
          </pre>
        </Card>
      )}
    </div>
  );
}

function DimensionsTable({ results }: { results: unknown[] | null }) {
  if (!results) return <p className="text-sm text-slate-500">No results yet.</p>;
  const rows = results as Array<{
    dimension_id: string;
    dimension_name: string;
    score: number;
    weight: number;
    summary: string;
    error: string | null;
  }>;
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="pb-2 pr-4 font-medium">Dimension</th>
          <th className="pb-2 pr-4 font-medium">Score</th>
          <th className="pb-2 font-medium">Summary</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
        {rows.map((r) => (
          <tr key={r.dimension_id} className="align-top">
            <td className="py-2 pr-4 font-medium">{r.dimension_name}</td>
            <td className="py-2 pr-4 font-mono">{r.error ? '—' : `${r.score}/10`}</td>
            <td className="py-2 text-slate-600 dark:text-slate-400">
              {r.error ? <span className="text-[var(--color-critical)]">{r.error.slice(0, 140)}</span> : r.summary}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface FixSuggestion {
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  estimated_effort: string | null;
}

function FixPlan({ results }: { results: unknown[] | null }) {
  if (!results || results.length === 0) {
    return <p className="text-sm text-slate-500">Fix plan unavailable — audit results haven't loaded yet.</p>;
  }
  const dims = results as Array<{
    dimension_id: string;
    dimension_name: string;
    fix_suggestions?: FixSuggestion[];
  }>;

  const PRIORITY_ORDER: Record<FixSuggestion['priority'], number> = {
    high: 0,
    medium: 1,
    low: 2,
  };

  const items: Array<FixSuggestion & { dimension_name: string }> = [];
  for (const d of dims) {
    for (const fix of d.fix_suggestions ?? []) {
      items.push({ ...fix, dimension_name: d.dimension_name });
    }
  }
  items.sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]);

  if (items.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Nothing to fix</CardTitle>
          <CardDescription>This audit produced no actionable fix suggestions. Either the audit is squeaky clean, or the dimensions surfaced findings without specific fixes — check the Findings tab.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const PRIORITY_STYLES: Record<FixSuggestion['priority'], string> = {
    high: 'bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200',
    medium: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
    low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">
        {items.length} suggestion{items.length === 1 ? '' : 's'}, sorted by priority. Address the high-priority items first.
      </p>
      {items.map((fix, idx) => (
        <Card key={`${fix.dimension_name}-${idx}`}>
          <div className="mb-2 flex items-center gap-2">
            <span className={cn('rounded-full px-2 py-0.5 text-xs uppercase tracking-wide', PRIORITY_STYLES[fix.priority])}>
              {fix.priority}
            </span>
            <span className="font-mono text-xs text-slate-500">{fix.dimension_name}</span>
            {fix.estimated_effort && (
              <span className="ml-auto text-xs text-slate-500">~ {fix.estimated_effort}</span>
            )}
          </div>
          <h3 className="font-semibold">{fix.title}</h3>
          <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{fix.description}</p>
        </Card>
      ))}
    </div>
  );
}

function FindingsList({ findings }: { findings: FindingOut[] }) {
  const severities = ['critical', 'high', 'medium', 'low', 'info'] as const;
  const [active, setActive] = useState<Set<FindingOut['severity']>>(
    new Set<FindingOut['severity']>(['critical', 'high', 'medium', 'low', 'info']),
  );

  const toggle = (s: FindingOut['severity']) => {
    const next = new Set(active);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setActive(next);
  };

  const filtered = useMemo(
    () => [...findings].filter((f) => active.has(f.severity)).sort(
      (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity],
    ),
    [findings, active],
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {severities.map((s) => (
          <button
            key={s}
            onClick={() => toggle(s)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs capitalize transition-colors',
              active.has(s)
                ? 'border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900'
                : 'border-slate-200 text-slate-600 dark:border-slate-800 dark:text-slate-400',
            )}
          >
            {s} ({findings.filter((f) => f.severity === s).length})
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-slate-500">No findings match the current filters.</p>
      ) : (
        filtered.map((f) => (
          <Card key={f.id}>
            <div className="mb-2 flex items-center gap-2">
              <span className={cn('rounded-full px-2 py-0.5 text-xs uppercase tracking-wide', SEVERITY_STYLES[f.severity])}>
                {f.severity}
              </span>
              <span className="font-mono text-xs text-slate-500">{f.dimension_id}</span>
            </div>
            <h3 className="font-semibold">{f.title}</h3>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{f.description}</p>
          </Card>
        ))
      )}
    </div>
  );
}
